import { ChangeEventHandler, MouseEventHandler, useRef, useState } from 'react'

import classNames from 'classnames'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { CharacterCounter } from '@/Atomic/Atoms/CharacterCounter/CharacterCounter'
import { AiDescriptionField } from '@/Atomic/Molecules/AiDescriptionField'
import { Button } from '@/Atomic/Molecules/Button'
import { DisplayField } from '@/Atomic/Molecules/DisplayField'
import { FileFormatBadge } from '@/Atomic/Molecules/FileFormatBadge'
import { DateTimePicker } from '@/Atomic/Molecules/Inputs/DateTimePicker'
import { Input } from '@/Atomic/Molecules/Inputs/Input'
import { AsyncSelect } from '@/Atomic/Molecules/Inputs/Select/AsyncSelect'
import { Select } from '@/Atomic/Molecules/Inputs/Select/Select'
import { Textarea } from '@/Atomic/Molecules/Inputs/Textarea'
import { errorToast } from '@/Components/Toast'
import { DataClassName } from '@/Data/const'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { useOnEscPress } from '@/hooks/useOnEscPress'
import { FieldMap } from '@/utils/CRUD/read/dataItem/types'
import { getEnumGroup } from '@/utils/enumGroupVariant'
import { isString } from '@/utils/isString'

import './InlineCustomField.scss'

interface FieldProps {
  dataItem: DataItem
  fieldMap: FieldMap
  className?: string
  editingField: string
  setEditingField: React.Dispatch<React.SetStateAction<string>>
  isLoading?: boolean
}

export const InlineCustomField: React.FC<FieldProps> = ({
  dataItem,
  fieldMap,
  className,
  editingField,
  setEditingField,
  isLoading,
}) => {
  const { t } = useTranslation()
  const ref = useRef(null)
  const isEditing = editingField === fieldMap.name

  const {
    name,
    type,
    value,
    displayValue,
    isMultiline,
    enumValueOptions,
    fieldSchema,
    reference: { referenceDataItem, loadReferenceOptions } = {},
  } = fieldMap

  const isReadonly = fieldSchema?.readonly ?? true
  const maxLength = fieldSchema?.maxLength
  const isDocumentFormatField =
    dataItem.dataClassName() === DataClassName.DOCUMENT &&
    name === 'format' &&
    isString(displayValue)
  const isListDescriptionField =
    dataItem.dataClassName() === DataClassName.LIST &&
    name === 'description' &&
    (dataItem.get('type') as string | undefined) === 'dynamic'

  const onEditClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    setEditingField(fieldMap.name)
  }

  const [newValue, setNewValue] = useState(value)
  const [valueLength, setValueLength] = useState(0)

  const cancelEditing = () => {
    setEditingField('')
  }

  const handleSave = () => {
    if (isEditing) {
      dataItem
        .update({ [name]: newValue })
        .then(cancelEditing)
        .catch((e) => errorToast(e))
    }
  }

  const onInputChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setNewValue(event.target.value)
    setValueLength(event.target.value.length)
  }

  const onDropdownChange = (value: unknown) => {
    if (value === null) {
      return setNewValue('')
    }
    setNewValue(value)
  }

  useOnClickOutside({ elementRef: ref, onClick: handleSave })
  useOnEscPress({ onEscPress: cancelEditing })

  const onKeyUp: React.KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      handleSave()
    }

    if (e.key === 'Escape') {
      cancelEditing()
    }
  }

  if (isListDescriptionField) {
    return <AiDescriptionField dataItem={dataItem} fieldMap={fieldMap} />
  }

  if (!isEditing) {
    return (
      <span
        className={classNames('inline-display-value', { hover: !isEditing })}
      >
        {isDocumentFormatField ? (
          <FileFormatBadge format={displayValue} />
        ) : (
          <DisplayField
            type={type}
            value={displayValue}
            referenceDataItem={referenceDataItem}
            variant="info"
            enumGroup={getEnumGroup(fieldSchema?.enumValues, value)}
          />
        )}
        <Button
          icon="edit"
          buttonClassName={classNames('edit-icon', { 'd-none': isReadonly })}
          onClick={onEditClick}
          tooltip={t('tooltip.edit')}
        />
      </span>
    )
  }

  if (type === 'boolean') {
    return (
      <Select
        name={name}
        label=" "
        options={[
          { value: true, label: t('common.yes') },
          { value: false, label: t('common.no') },
        ]}
        defaultValue={value}
        onChange={onDropdownChange}
        className={classNames(className)}
        isDisabled={isReadonly}
      />
    )
  }

  if (type === 'enum' || type === 'enumarray') {
    const isMultiSelect = type === 'enumarray'

    return (
      <div ref={ref} onClick={(e) => e.stopPropagation()}>
        <Select
          name={name}
          label=" "
          options={enumValueOptions}
          defaultValue={value}
          onChange={onDropdownChange}
          className={classNames(className, { 'multi-enum': isMultiSelect })}
          isMulti={isMultiSelect}
          isDisabled={isReadonly}
          autoFocus
          openMenuOnFocus
          isClearable
        />
      </div>
    )
  }

  if (type === 'reference') {
    return (
      <div ref={ref} onClick={(e) => e.stopPropagation()}>
        <AsyncSelect
          name={name}
          label=" "
          displayValue={displayValue as string}
          defaultValue={value}
          onChange={onDropdownChange}
          loadReferenceOptions={loadReferenceOptions}
          className={className}
          isDisabled={isReadonly}
          autoFocus
          openMenuOnFocus
        />
      </div>
    )
  }

  if (type === 'date' || type === 'datetime') {
    return (
      <div ref={ref} onClick={(e) => e.stopPropagation()}>
        <DateTimePicker
          name={name}
          title=" "
          onChange={onDropdownChange}
          defaultValue={dayjs(value as string)}
          className={className}
          onAccept={handleSave}
          onClose={cancelEditing}
          open
          autoFocus
        />
      </div>
    )
  }

  if (type === 'string' && isMultiline) {
    return (
      <div className="inline text field">
        <Textarea
          name={name}
          label=" "
          defaultValue={value as string}
          onChange={onInputChange}
          className="full-width"
          disabled={isReadonly || isLoading}
          autoFocus
        />
        {/* TODO: fix textarea later */}
        {maxLength && maxLength > 0 && (
          <div style={{ alignSelf: 'flex-end' }}>
            <CharacterCounter
              count={valueLength}
              maxLength={maxLength}
              isOverLimit={valueLength > maxLength}
            />
          </div>
        )}

        <div className="buttons g-8">
          <Button
            variant="icon"
            icon="cross"
            className="edit-button"
            onClick={cancelEditing}
            tooltip={t('tooltip.cancel')}
          />
          <Button
            variant="icon"
            icon="check"
            className="edit-button"
            onClick={handleSave}
            tooltip={t('tooltip.save')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="d-flex">
      <Input
        name={name}
        label=" "
        unstyledInputWrapper
        defaultValue={value as string}
        className="full-width"
        inputWrapperClassName="inline input field wrapper"
        inputClassName="inline input field"
        disabled={isReadonly || isLoading}
        onKeyUp={onKeyUp}
        autoFocus
        onChange={onInputChange}
      />
      <Button
        variant="icon"
        icon="cross"
        className="inline input edit cancel"
        onClick={cancelEditing}
        tooltip={t('tooltip.cancel')}
      />
      <Button
        variant="icon"
        icon="check"
        className="inline input edit confirm"
        onClick={handleSave}
        tooltip={t('tooltip.save')}
      />
    </div>
  )
}
